import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button
} from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import creditCardChargesMapper, {
  CreditCardCharge
} from 'models/Charge/CreditCardCharges'

import Can from 'components/Can'
import { Table } from 'components/Table'

import { theme } from 'styles/theme'

import { ModalChargeDetailContext } from '../../context'
import { cardTittle } from '../../ModalChargeDetail.utils'

const Attemps: React.FC = () => {
  const { invoice, updateReload, reload } = useContext(ModalChargeDetailContext)
  const [retrying, setRetrying] = useState(false)

  const attempts = invoice?.creditCardCharges ?? []
  const hasAttempts = attempts.length > 0

  // Cobrança que aceita cartão mostra o accordion mesmo sem tentativa nenhuma: antes ele
  // só aparecia depois da primeira tentativa, e a ausência do bloco era lida como "o
  // sistema não vai cobrar". Sem cartão como meio de pagamento o accordion não faz
  // sentido e continua escondido.
  //
  // `paymentType` é multivalorado separado por ';' (ex.
  // "pix;boleto;credit_card_recurring;boleto_pix"), e aqui o `includes('credit_card')`
  // casar também com credit_card_recurring/credit_card_installments é intencional: as
  // três aceitam cartão. Quem separa as variantes é o `isRecurringCharge` abaixo.
  const acceptsCreditCard =
    invoice?.paymentTypeDetails?.credit_card?.enabled ||
    invoice?.paymentType?.includes('credit_card') ||
    false

  if (!hasAttempts && !acceptsCreditCard) {
    return null
  }

  // Cobrança automática exige as DUAS coisas: a cobrança ser de cartão recorrente E ter
  // cartão vinculado à recorrência. Em `credit_card` avulso ou `credit_card_installments`
  // não há batch nenhum — quem paga é o cliente entrando na fatura — então prometer
  // tentativa automática ali seria mentira.
  //
  // `source: 'recurrence'` é vínculo ativo; 'charged' é só o cartão que já pagou a fatura.
  const isRecurringCharge =
    invoice?.paymentType?.includes('credit_card_recurring') || false
  const willChargeAutomatically =
    isRecurringCharge && invoice?.linkedCreditCard?.source === 'recurrence'

  const hasFailedCreditCardCharge = attempts.some(
    (charge) => charge.status === 'unathorized'
  )
  const showRetry =
    invoice?.status === 'open_payment' && hasFailedCreditCardCharge

  async function handleRetryCharge() {
    setRetrying(true)
    const id = toast.loading('Retentando cobrança...')

    const response = await DataService({
      type: 'POST',
      url: `/v1/billings/${invoice?.uuid}/retry_credit_card`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error
        ? errorMessage || 'Não foi possível processar a cobrança'
        : response.data.message,
      type: response.error ? 'error' : 'success',
      isLoading: false,
      autoClose: 5000
    })

    setRetrying(false)

    if (!response.error) {
      updateReload(!reload)
    }
  }

  return (
    <Accordion
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary aria-controls="payerDetails-content" id="payerDetails">
        {cardTittle(
          'Tentativas de cobrança',
          'storefront',
          `${theme.palette.primary.main}`
        )}
      </AccordionSummary>
      <AccordionDetails>
        {hasAttempts ? (
          <Table
            mobileCards
            header={[
              { id: 'chargedAt', label: 'Data' },
              { id: 'status', label: 'Status' },
              { id: 'description', label: 'Descrição' },
              { id: 'statusDetail', label: 'Motivo' }
            ]}
            rows={creditCardChargesMapper(
              invoice?.creditCardCharges as CreditCardCharge
            )}
            select={{ useSelect: false }}
            hiddePagination
          />
        ) : (
          // Só promete cobrança automática quando ela existe de fato (recorrente com
          // cartão vinculado); nos outros casos diz que o pagamento depende do cliente,
          // que é o que acontece.
          //
          // Sem hora no texto de propósito: o horário do batch é detalhe de
          // implementação (hoje o Zoop::ZoopChargeCreditCardRecurringBatchWorker roda às
          // 10h) e prometer hora na tela cria expectativa que uma mudança de cron
          // quebraria sem ninguém lembrar de corrigir aqui.
          //
          // Alinhado à esquerda: é frase corrida, não rótulo curto, e centralizar deixava
          // a segunda linha desalinhada do resto do accordion. `smLight` + #777777 é o
          // texto secundário deste modal (mesmo do InternalIdentifier).
          <Typography variant="smLight" color="#777777" marginTop="10px">
            {willChargeAutomatically
              ? 'Nenhuma tentativa de cobrança ainda. Há cartão recorrente cadastrado, então a cobrança é tentada automaticamente no dia do vencimento.'
              : 'Nenhuma tentativa de cobrança ainda. O pagamento depende do cliente informar o cartão no link da fatura.'}
          </Typography>
        )}

        {showRetry && (
          <Can permission="billings:manage">
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Tooltip
                title={
                  !invoice?.canManualRetry
                    ? invoice?.manualRetryReason || ''
                    : ''
                }
              >
                <span>
                  <Button
                    variantButton="action"
                    onClick={handleRetryCharge}
                    disabled={!invoice?.canManualRetry || retrying}
                    loading={retrying}
                  >
                    Retentar cobrança
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Can>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default Attemps
